/**
 * Yönetici Aday Rapor Detayı — Spec Bölüm 7.4 + 8 + S3.3
 *
 *   /yonetici/sinavlar/:examId/aday/:attemptId
 *
 * Yönetici görünümünde:
 *  - Aday metadata (anonim modda gizli)
 *  - Skor + level + sertifika
 *  - AI markdown rapor
 *  - Konu bazlı bar chart
 *  - Soru bazlı detay tablosu (cevap + süre + near-miss)
 */
import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'wouter';
import { Header } from '../components/Header.js';
import { Alert, Card } from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/auth-client.js';
import { ReportMarkdown } from '../components/ReportMarkdown.js';

interface ResponseRow {
  id: string;
  questionId: string;
  questionCode: string;
  questionTopic: string;
  questionFormat: string;
  selectedOption: string | null;
  numericAnswer: number | null;
  isCorrect: boolean;
  pointsAwarded: number;
  nearMissCategory: string | null;
  errorPercentage: number | null;
  timeSpentSec: number;
  isFlagged: boolean;
  flagNote: string | null;
}

interface AttemptDetail {
  id: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  totalScore: number | null;
  scoreLevel: string | null;
  topicScores: Record<string, number> | null;
  candidateReportMarkdown: string | null;
  candidateReportGeneratedAt: string | null;
  candidateReportCostUsd: number | null;
  certificateId: string | null;
  certificateValidUntil: string | null;
  tabSwitchCount: number;
  candidate: {
    fullName: string | null;
    email: string | null;
    anonymousId: string | null;
    position: string | null;
    experienceYears: string;
  };
  responses: ResponseRow[];
}

interface DetailResponse {
  attempt: AttemptDetail;
  summary: {
    responseCount: number;
    correctCount: number;
    flaggedCount: number;
    avgTimeSec: number;
    totalQuestions: number;
  };
  anonymousMode: boolean;
}

const TOPIC_LABELS: Record<string, string> = {
  PM: 'Parça Modelleme',
  AS: 'Assembly',
  DR: 'Drawing',
  SK: 'Sketch',
  SM: 'Sac Metal',
  WD: 'Weldments',
  SF: 'Surfacing',
  CF: 'Configurations',
  GN: 'Genel SW',
  DT: 'Tasarım Teorisi',
};

const NEAR_MISS_LABELS: Record<string, string> = {
  exact: 'Tam',
  near_miss: 'Yakın (½)',
  close_wrong: 'Yanlış-yakın (¼)',
  wrong: 'Yanlış',
};

const EXP_LABELS: Record<string, string> = {
  LT_1Y: '<1 yıl',
  Y1_TO_2: '1-2 yıl',
  Y2_TO_5: '2-5 yıl',
  GT_5Y: '5+ yıl',
};

export function ExamAttemptDetailPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const { data: session, isPending: sessionPending } = useSession();
  const params = useParams<{ examId: string; attemptId: string }>();
  const examId = params.examId ?? '';
  const attemptId = params.attemptId ?? '';
  const [data, setData] = useState<DetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sessionPending) return;
    if (!session?.user) {
      setLocation('/yonetici/giris');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await api.get<DetailResponse>(
          `/api/manager/exams/${examId}/attempts/${attemptId}`,
        );
        if (!cancelled) setData(result);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setError(err.message);
        else setError('Detaylar yüklenemedi');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [examId, attemptId, session, sessionPending, setLocation]);

  if (sessionPending || (!data && !error)) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-4xl px-6 py-12">
          <p className="text-slate-500">Yükleniyor…</p>
        </main>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-4xl px-6 py-12">
          <Alert variant="danger">{error ?? 'Aday bulunamadı'}</Alert>
        </main>
      </div>
    );
  }

  const { attempt, summary, anonymousMode } = data;
  const displayName =
    attempt.candidate.anonymousId ??
    attempt.candidate.fullName ??
    'Aday';
  const topicScores = attempt.topicScores ?? {};

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-4xl px-6 py-12">
        <button
          type="button"
          onClick={() => setLocation(`/yonetici/sinavlar/${examId}/adaylar`)}
          className="text-sm font-medium text-blue-900 hover:underline"
        >
          ← Adaylar
        </button>

        <h1 className="mt-2 text-3xl font-bold text-slate-900">
          {displayName}
        </h1>
        <div className="mt-1 text-sm text-slate-600">
          {!anonymousMode && attempt.candidate.email && (
            <span>{attempt.candidate.email} · </span>
          )}
          {attempt.candidate.position && (
            <span>{attempt.candidate.position} · </span>
          )}
          <span>
            {EXP_LABELS[attempt.candidate.experienceYears] ??
              attempt.candidate.experienceYears}{' '}
            deneyim
          </span>
        </div>

        {/* Skor + Sertifika */}
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <Metric
            label="Skor"
            value={
              attempt.totalScore !== null ? `${attempt.totalScore}/100` : '—'
            }
          />
          <Metric label="Seviye" value={attempt.scoreLevel ?? '—'} />
          <Metric
            label="Sertifika"
            value={attempt.certificateId ?? '—'}
            mono
          />
        </div>

        {/* Cevap özet metrikler */}
        <div className="mt-4 grid gap-4 sm:grid-cols-4">
          <Metric
            label="Cevaplanan"
            value={`${summary.responseCount}/${summary.totalQuestions}`}
          />
          <Metric label="Doğru" value={String(summary.correctCount)} />
          <Metric
            label="Ort. Süre"
            value={`${summary.avgTimeSec}s`}
          />
          <Metric
            label="Sekme Değiş."
            value={String(attempt.tabSwitchCount)}
          />
        </div>

        {/* Konu breakdown */}
        {Object.keys(topicScores).length > 0 && (
          <Card className="mt-6">
            <h2 className="text-lg font-semibold text-slate-900">
              Konu Bazlı Performans
            </h2>
            <div className="mt-4 space-y-3">
              {Object.entries(topicScores)
                .sort(([, a], [, b]) => b - a)
                .map(([topic, pct]) => (
                  <div key={topic}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-slate-700">
                        {TOPIC_LABELS[topic] ?? topic}
                      </span>
                      <span className="font-mono text-slate-900">{pct}</span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${pct}%`,
                          background:
                            pct >= 75
                              ? '#15803d'
                              : pct >= 50
                              ? '#0369a1'
                              : pct >= 30
                              ? '#ca8a04'
                              : '#dc2626',
                        }}
                      />
                    </div>
                  </div>
                ))}
            </div>
          </Card>
        )}

        {/* AI Rapor */}
        <Card className="mt-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">AI Rapor</h2>
            {attempt.candidateReportCostUsd !== null && (
              <span className="text-xs text-slate-500">
                {attempt.candidateReportCostUsd === 0
                  ? 'mock'
                  : `$${attempt.candidateReportCostUsd.toFixed(4)}`}
                {attempt.candidateReportGeneratedAt &&
                  ` · ${new Date(attempt.candidateReportGeneratedAt).toLocaleString('tr-TR')}`}
              </span>
            )}
          </div>
          <div className="mt-3">
            {attempt.candidateReportMarkdown ? (
              <ReportMarkdown text={attempt.candidateReportMarkdown} />
            ) : (
              <Alert variant="info">
                Rapor henüz üretilmedi. Worker birkaç saniye içinde
                tamamlayacak — sayfayı yenileyin.
              </Alert>
            )}
          </div>
        </Card>

        {/* Soru bazlı detay tablosu */}
        <Card className="mt-6">
          <h2 className="text-lg font-semibold text-slate-900">
            Soru Detayları
          </h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="py-2 pr-3">Soru</th>
                  <th className="py-2 pr-3">Konu</th>
                  <th className="py-2 pr-3">Cevap</th>
                  <th className="py-2 pr-3">Sonuç</th>
                  <th className="py-2 pr-3 text-right">Puan</th>
                  <th className="py-2 pl-3 text-right">Süre</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {attempt.responses.map((r) => (
                  <tr key={r.id}>
                    <td className="py-2 pr-3 font-mono text-xs text-slate-700">
                      {r.questionCode}
                    </td>
                    <td className="py-2 pr-3 text-slate-600">
                      {TOPIC_LABELS[r.questionTopic] ?? r.questionTopic}
                    </td>
                    <td className="py-2 pr-3 font-mono text-slate-800">
                      {r.selectedOption ??
                        (r.numericAnswer !== null ? r.numericAnswer : '—')}
                    </td>
                    <td className="py-2 pr-3">
                      {r.isCorrect ? (
                        <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-800">
                          Doğru
                        </span>
                      ) : r.nearMissCategory ? (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                          {NEAR_MISS_LABELS[r.nearMissCategory] ??
                            r.nearMissCategory}
                        </span>
                      ) : (
                        <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700">
                          Yanlış
                        </span>
                      )}
                      {r.isFlagged && (
                        <span
                          className="ml-1 inline-block"
                          title={r.flagNote ?? 'Aday itiraz etti'}
                        >
                          🚩
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono">
                      {r.pointsAwarded}
                    </td>
                    <td className="py-2 pl-3 text-right text-slate-500">
                      {r.timeSpentSec}s
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Sertifika linki */}
        {attempt.certificateId && (
          <Card className="mt-6">
            <h2 className="text-lg font-semibold text-slate-900">
              Sertifika
            </h2>
            <p className="mt-2 text-sm text-slate-700">
              Aday {NEAR_MISS_LABELS ? '' : ''}
              <strong>{attempt.scoreLevel}</strong> seviyesini geçti.
              {attempt.certificateValidUntil && (
                <>
                  {' '}
                  Sertifika{' '}
                  {new Date(
                    attempt.certificateValidUntil,
                  ).toLocaleDateString('tr-TR')}{' '}
                  tarihine kadar geçerli.
                </>
              )}
            </p>
            <Link
              href={`/sertifika/${attempt.certificateId}`}
              className="mt-3 inline-block rounded-md border border-blue-900 px-3 py-1.5 text-sm font-medium text-blue-900 hover:bg-blue-50"
            >
              Sertifika sayfasını gör →
            </Link>
          </Card>
        )}
      </main>
    </div>
  );
}

function Metric({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 text-center">
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p
        className={`mt-1 text-2xl font-bold text-slate-900 ${
          mono ? 'font-mono text-base' : ''
        }`}
      >
        {value}
      </p>
    </div>
  );
}
